import React, { useEffect, useState } from "react";
import Card from "components/card";
import { MdCreate, MdDelete } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import { getPlacementImage, addPlacementImage, updatePlacementImage, deletePlacementImage } from "Services/allApi";
import Swal from "sweetalert2";

function Placement() {
    const [placementImages, setPlacementImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [imageIdToUpdate, setImageIdToUpdate] = useState(null);

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        setLoading(true);
        try {
            const response = await getPlacementImage();
            setPlacementImages(response.data || []);
        } catch (error) {
            setError(error.message || "Failed to fetch images.");
            toast.error("Error fetching placement images!");
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleAddPlacementImage = async () => {
        if (!selectedImage) {
            toast.error("Please select an image first!");
            return;
        }

        const formData = new FormData();
        formData.append("image", selectedImage);

        setUploading(true);
        try {
            await addPlacementImage(formData);
            toast.success("Placement image added successfully!");
            closeModal();
            fetchImages();
        } catch (error) {
            toast.error("Failed to add placement image.");
        } finally {
            setUploading(false);
        }
    };

    const handleUpdatePlacementImage = async () => {
        if (!selectedImage || !imageIdToUpdate) {
            toast.error("Please select an image first!");
            return;
        }

        const formData = new FormData();
        formData.append("image", selectedImage);

        setUploading(true);
        try {
            await updatePlacementImage(imageIdToUpdate, formData);
            toast.success("Placement image updated successfully!");
            closeModal();
            fetchImages();
        } catch (error) {
            toast.error("Failed to update placement image.");
        } finally {
            setUploading(false);
        }
    };

    const handleDeletePlacementImage = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            customClass: {
                popup: 'dark:bg-gray-800 dark:text-white', // Dark background and white text
                title: 'dark:text-white',
                confirmButton: 'dark:bg-blue-500 dark:hover:bg-blue-700 dark:text-white',
                cancelButton: 'dark:bg-red-500 dark:hover:bg-red-700 dark:text-white'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deletePlacementImage(id);
                    toast.success("Placement image deleted successfully!");
                    fetchImages();
                } catch (error) {
                    toast.error("Failed to delete placement image.");
                }
            }
        });
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedImage(null);
        setImagePreview(null);
        setIsModalOpen(true);
    };

    const openEditModal = (image) => {
        setIsEditMode(true);
        setImageIdToUpdate(image.id);
        setSelectedImage(null);
        setImagePreview(image.image);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage(null);
        setImagePreview(null);
        setIsEditMode(false);
    };

    return (
        <>
            <div className="mb-4 flex justify-end mt-4">
                <button onClick={openAddModal} className="bg-purple-800 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-700">
                    Add Placement
                </button>
            </div>

            {loading ? (
                <p className="text-center">Loading...</p>
            ) : error ? (
                <p className="text-center text-red-600">Error: {error}</p>
            ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {placementImages.length > 0 ? (
                        placementImages.map((image) => (
                            <Card key={image.id} className="w-[230px] h-[320px] flex flex-col justify-between p-2">
                                <div className="flex flex-col h-full">
                                    <div className="relative w-full h-[250px] mb-2">
                                        <img src={image.image || "default-placeholder.jpg"} alt="Placement" className="w-full h-full object-cover rounded-xl border" />
                                    </div>
                                    <div className="flex justify-between">
                                        <button onClick={() => openEditModal(image)}>
                                            <MdCreate className="text-xl text-green-700" />
                                        </button>
                                        <button onClick={() => handleDeletePlacementImage(image.id)}>
                                            <MdDelete className="text-xl text-red-700" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))
                    ) : (
                        <p className="text-center text-gray-600">No placement images available.</p>
                    )}
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg max-w-lg w-full dark:!bg-navy-700 dark:text-white">
                        <h2 className="text-lg font-semibold mb-4">{isEditMode ? "Edit Placement" : "Add Placement"}</h2>

                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="block w-full mb-2 border rounded-lg cursor-pointer"
                        />

                        {/* Description about image dimensions */}
                        <div className="mb-3">
                            <p className="text-xs text-gray-600">The height and width must be 1080 × 1350</p>
                        </div>

                        {/* Image Preview */}
                        {imagePreview && (
                            <div className="mb-4">
                                <img src={imagePreview} alt="Preview" className="h-32 w-32 object-cover rounded-lg" />
                            </div>
                        )}

                        <div className="mt-4 flex justify-end">
                            <button onClick={closeModal} className="bg-gray-300 px-4 py-2 rounded-lg mr-2">Cancel</button>
                            <button onClick={isEditMode ? handleUpdatePlacementImage : handleAddPlacementImage} disabled={uploading} className="bg-purple-800 text-white px-4 py-2 rounded-lg">
                                {uploading ? "Uploading..." : isEditMode ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>

                </div>
            )}

            <ToastContainer position="top-center" autoClose={5000} />
        </>
    );
}

export default Placement;
